import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, shareReplay, take, tap } from 'rxjs/operators';
import { OrderType } from '../../models/core/order-type.enum';
import { PaymentType } from '../../models/ordering/payment-type.model';
import { DeserializationUtils } from '../../utils/deserialization-utils';
import { AccountService } from '../account/account.service';
import { ConfigService } from '../core/config.service';
import { PaymentTypeFee } from '../../models/ordering/payment-type-fee.model';

@Injectable({ providedIn: 'root' })
export class OrderPaymentTypesService {
  private paymentTypes: BehaviorSubject<PaymentType[]> = new BehaviorSubject(<PaymentType[]>[]);
  public paymentTypes$: Observable<PaymentType[]> = this.paymentTypes.asObservable();

  constructor(private http: HttpClient, private accountService: AccountService, private configService: ConfigService) {
    this.accountService.userLogin$.pipe(mergeMap(() => this.fetchPaymentTypes())).subscribe(
      () => {},
      () => {}
    );
  }

  public fetchPaymentTypes(noContact = false, orderType?: OrderType): Observable<PaymentType[]> {
    return this.shouldFetchNoContactPaymentTypes(noContact, orderType).pipe(
      mergeMap((shouldUseNoContact: boolean) => {
        let params: HttpParams = new HttpParams();
        if (shouldUseNoContact) {
          params = params.set('noContact', 'true');
        }
        return this.http.get('/ws/integrated/v1/ordering/order/paymentTypes', { params }).pipe(
          map((json: Object[]) => DeserializationUtils.deserializeArray(json, PaymentType)),
          catchError(() => of([])),
          tap((paymentTypes: PaymentType[]) => this.paymentTypes.next(paymentTypes)),
          shareReplay({ bufferSize: 1, refCount: true })
        );
      })
    );
  }

  public fetchPaymentTypeFee(paymentType: string, amount: string): Observable<PaymentTypeFee> {
    let params: HttpParams = new HttpParams();
    params = params.set('paymentType', paymentType);
    params = params.set('amount', amount);
    return this.http
      .get('/ws/integrated/v1/ordering/order/payments/getFee', { params })
      .pipe(map((json: Object) => DeserializationUtils.deserializeObj(json, PaymentTypeFee)));
  }

  private shouldFetchNoContactPaymentTypes(noContact: boolean, orderType?: OrderType): Observable<boolean> {
    if (!noContact || !orderType) {
      return of(false);
    }

    return this.configService.findOrderTypeConfig(orderType).pipe(
      take(1),
      map(orderTypeConfig => !!orderTypeConfig?.webNoContactEnabled)
    );
  }
}
